<template>
    <vue-select
        ref="vueSelectSearchItem"
        placeholder="Search Inventory"
        :options="options"
        :filter="filter"
        :clearable="false"
        label="name"
        @search="search"
        @input="onSelect"
        :closeOnSelect="false"
        @close="onClose"
        class="search-item"
    >
        <template v-slot:option="{ name, uses_count }">
            <small
                ><i v-if="uses_count > 0" class="fas fa-sitemap mr-2"></i
            ></small>
            <b>{{ name }} </b>
        </template>

        <template v-slot:no-options="{ search }">
            <div class="no-opt">
                <span v-if="search === ''">
                    Type to search
                </span>
                <span v-else-if="isPreloading">
                    Loading...
                </span>
                <span v-else>
                    No result
                </span>
            </div>
        </template>

        <template v-slot:open-indicator>
            <!-- this empty div is intentional, do not remove. this is for a "replacement" to default arrow -->
            <div></div>
        </template>

        <template v-slot:spinner="{ loading }">
            <img
                v-if="loading"
                class="spinner"
                src="/apps/assets/img/ajax-spinner.gif"
            />
        </template>

        <template v-slot:list-footer>
            <li ref="footerLoader" class="footerloader" v-if="hasNextPage">
                Loading...
            </li>
        </template>
    </vue-select>
</template>

<script>
// TODO: after search (user select item), re-set tab position to the first tab
import vueSelect from "vue-select";
import { debounce, concat } from "lodash";

const ITEM_SEARCH_URI = `${process.env.BARRAMUNDI}inventory/search`;
const PER_PAGE = 20;

/**
 * @name VetSelectItem
 * This component is the replicate of vet-select-patient
 * TODO: Based on the vet-select componentns for all modules, seem we put the same method in to a mixin
 */
export default {
    components: {
        vueSelect
    },
    props: {
        focusOnload: {
            default: false,
            type: Boolean
        }
    },
    watch: {
        options(options = []) {
            let maxLength = 0;
            let ul = document.querySelector(".search-item ul");

            if (options.length === 0) {
                ul.style.width = "";
                ul.style.left = "";
                ul.style.right = "";
                ul.style.borderTopLeftRadius = "";
                ul.style.borderTopRightRadius = "";
            } else {
                for (let option of options) {
                    let nameLength = option.name.length;

                    if (nameLength > maxLength) {
                        maxLength = nameLength;
                    }
                }

                // capped maxlength to 350 , because it will go out of screen for mobile
                let maxLengthValue = maxLength * 11;
                ul.style.width = `${
                    maxLengthValue < 350 ? maxLengthValue : 350
                }px`;
                ul.style.left = "unset";
                ul.style.right = "0";
                ul.style.borderTopLeftRadius = "5px";
                ul.style.borderTopRightRadius = "5px";
            }
        }
    },
    data() {
        return {
            query: "",
            options: [],
            observer: null,
            hasNextPage: false,
            isPreloading: false,
            currentPage: 1
        };
    },
    mounted() {
        this.observer = new IntersectionObserver(this.infiniteScroll);

        if (this.focusOnload) {
            this.focus();
        }
    },
    methods: {
        focus() {
            this.$refs.vueSelectSearchItem.$el.querySelector("input").focus();
        },
        search(value, loading) {
            this.hasNextPage = false;

            if (value.length >= 2) {
                this.currentPage = 1;
                this.isPreloading = true;
                this.query = value;

                if (value === "") {
                    this.isPreloading = false;
                    this.options = [];
                    this.hasNextPage = false;
                    return;
                }

                this.fetchOptions(value, this, loading);
            } else {
                this.options = [];
                return;
            }
        },
        fetchOptions: debounce(async (value, self, loading) => {
            loading(true);

            const res = await self.$http.get(ITEM_SEARCH_URI, {
                params: {
                    query: value,
                    page: 1
                }
            });

            let results = res.data.response;

            if (results.message.code === 200) {
                let totalResult = results.total;

                delete results.total;
                delete results.message;

                self.options = Object.values(results);

                if (totalResult === PER_PAGE) {
                    self.hasNextPage = true;
                } else {
                    self.hasNextPage = false;
                }
            } else {
                self.hasNextPage = false;
                self.options = [];
            }

            loading(false);
            self.isPreloading = false;
            await self.registerFootLoader();
        }, 350),
        filter(options, search) {
            /**
             * if you ever wonder what this function does,
             * it basically loops through all options available
             * and then combine firstname, lastname and email of each option
             * to become a joined string, eg: "firstlastemail"
             * then it'll compare the search value with the joined string
             * and if it returns > -1, means the particular option
             * will be displayed in the dropdown list, and vice versa.
             */
            // prettier-ignore
            return options.filter(option => {
                let { name } = option;
                return (name || "").toLowerCase().indexOf(search.toLowerCase()) > -1;
            });
        },
        onSelect(value) {
            if (!value) {
                return;
            }
            this.$router.push(`/inventory/item/${value.id}`);
            this.onClose();
            window.scrollTo(0, 0); // reset scroll position
        },
        async registerFootLoader() {
            if (this.hasNextPage) {
                await this.$nextTick();
                this.observer.observe(this.$refs.footerLoader);
            }
        },
        onOpen() {
            this.registerFootLoader();
        },
        onClose() {
            this.query = "";
            this.observer.disconnect();
            this.options = []; // clear
            this.hasNextPage = false;
            this.$refs.vueSelectSearchItem.clearSelection();
            this.$refs.vueSelectSearchItem.$el.querySelector("input").blur();
        },
        async infiniteScroll([{ isIntersecting, target }]) {
            const self = this;

            // references: https://vue-select.org/guide/infinite-scroll.html
            if (isIntersecting && this.hasNextPage) {
                const ul = target.offsetParent;
                const scrollTop = target.offsetParent.scrollTop;
                await this.$nextTick();

                this.fetchNextPage(async () => {
                    await self.$nextTick();
                    ul.scrollTop = scrollTop; // keep scroll position after we append the options
                });
            }
        },
        async fetchNextPage(callback) {
            if (!this.hasNextPage) {
                return;
            }
            let self = this;
            self.currentPage++;

            const res = await this.$http.get(ITEM_SEARCH_URI, {
                params: {
                    page: self.currentPage,
                    query: self.query
                }
            });

            let results = res.data.response;

            if (results.message.code === 200) {
                let totalResult = results.total;

                delete results.total;
                delete results.message;

                self.options = concat(self.options, Object.values(results));

                if (totalResult == PER_PAGE) {
                    self.hasNextPage = true;
                } else {
                    self.hasNextPage = false;
                }

                if (callback) {
                    callback();
                }
            } else {
                self.hasNextPage = false;
                self.options = [];
            }
        }
    }
};
</script>

<style scoped>
.search-item ::-webkit-scrollbar {
    -webkit-appearance: none;
    width: 10px;
}

.search-item ::-webkit-scrollbar-thumb {
    border-radius: 5px;
    background-color: rgba(0, 0, 0, 0.5);
    -webkit-box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
}
.no-opt {
    padding: 0 5px;
}
.footerloader {
    padding: 3px 20px;
    font-style: italic;
}

/* CSS added here */
.search-item .vs__dropdown-option b {
    word-wrap: break-word;
    white-space: normal; /* ensures text can break into new line */
}
</style>
